import React, { useEffect, useState } from 'react';
import { DndContext, closestCenter, MouseSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, rectSortingStrategy } from '@dnd-kit/sortable';
import './DraggableContainer.css';
import SpinnerWheel from '../../../../Components/SpinnerWheel';
import { List } from '../../../../Components/List';
import { ProductItem } from './ProductItem';
import { demoteId, promoteId } from '../../Ranking/utils';

type DraggableContainerType = {
	dimension,
	elementToMap,
	handleOnDragEnd,
	onDelete,
	setNeedToReload,
	needToReload,
	items,
	setItems,
	searchTerm,
	searchProductsByIds,
	changeSecondCategorySelected,
	handleSelectCategorySelection,
};

export default function DraggableContainer({
	dimension,
	elementToMap,
	handleOnDragEnd,
	onDelete,
	setNeedToReload,
	needToReload,
	items,
	setItems,
	searchTerm,
	searchProductsByIds,
	handleSelectCategorySelection
}: DraggableContainerType) {

	const [filteredItems, setFilteredItems] = useState(items);
	const [isFiltered, setIsFiltered] = useState(false);

	useEffect(() => {
		const timer = setTimeout(() => {
			if (elementToMap.length > 0 && needToReload) {
				const productIds = elementToMap.map(item => item.id);
				getArrayOfProducts(productIds);
			}
		}, 800);
		return () => {
			clearTimeout(timer);
		};
	}, [elementToMap, needToReload]);

	useEffect(() => {
		if (searchTerm) {
			const filtered = items.filter(item =>
				item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
				item.id.toLowerCase().toString().includes(searchTerm.toLowerCase())
			);
			setFilteredItems(filtered);
			setIsFiltered(true);
		} else {
			setFilteredItems(items);
			setIsFiltered(false);
		}
	}, [searchTerm, items]);

	const sensors = useSensors(
		useSensor(MouseSensor),
		useSensor(TouchSensor)
	);

	function handleDragEnd(event) {
		const { active, over } = event;
		if (active.id !== over.id) {
			setItems((items) => {
				const oldIndex = items.findIndex((el) => el.id === active.id);
				const newIndex = items.findIndex((el) => el.id === over.id);
				const newItems = arrayMove(items, oldIndex, newIndex);
				handleOnDragEnd(newItems);
				return newItems;
			});
		}
	}

	function getArrayOfProducts(array) {

		searchProductsByIds(array, data => {
			if (data && data.products) {
				const updatedProducts = array.map(id => {
					const product = data.products.find(p => p.id === id);
					if (product) {
						return { id: product.id, name: product.name, urlThumbnail: product.urlThumbnail , categories : product.categories};
					}
					return null;
				}).filter(product => product !== null);
				setItems(updatedProducts);
				setNeedToReload(false);
			} else {
				console.error('No products found in the response');
			}
		});
	}

	return (
		<DndContext autoScroll={false} sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
			<SortableContext items={filteredItems} strategy={rectSortingStrategy}>
				{needToReload && <SpinnerWheel wheelSize="l" />}
				{filteredItems && filteredItems.length > 0 && (
					<>
						{isFiltered && dimension === promoteId &&
							<div className='h4'>Promoted products</div>
						}
						{isFiltered && dimension === demoteId &&
							<div className='h4'>Demoted products</div>
						}
						<List size="s" className="mb_20">
							{filteredItems.map((item) => ( 
								<ProductItem
									key={item.id}
									isFiltered={isFiltered}
									id={item.id}
									onDelete={onDelete}
									item={item}
									onSelectCategory={handleSelectCategorySelection}
								/>
							))}
						</List>
					</>
				)}
			</SortableContext>
		</DndContext>
	);
}